/**
 * useOpenReplay — App-wide session recording with auth identity.
 * Starts recording after auth completes (waits for SSO token cleanup),
 * stops on logout. Tags every session with coach identity.
 * No-op when FRONTEND_OPENREPLAY_KEY is absent.
 */

import { useEffect, useRef } from 'react';
import { getTracker } from '../utils/openreplay';
import { useAuth } from '../lib/auth';

export function useOpenReplay() {
  const { user, isAuthenticated, loading } = useAuth();
  const startedRef = useRef(false);

  useEffect(() => {
    if (!process.env.REACT_APP_OPENREPLAY_KEY || loading) return;

    const tracker = getTracker();
    if (!tracker) return;

    let cancelled = false; // StrictMode double-mount guard

    if (isAuthenticated && user && !tracker.isActive()) {
      tracker.start()
        .then((session) => {
          if (cancelled) return;
          if (session.success) {
            startedRef.current = true;

            // Identity: who is this coach?
            tracker.setUserID(user.email);

            // Metadata: searchable in OpenReplay dashboard
            if (user.name) tracker.setMetadata('name', user.name);
            const roleCode = user.role_code || user.role;
            if (roleCode) {
              tracker.setMetadata('role_code', roleCode);
              tracker.setMetadata('role', roleCode);
            }
            if (user.category) tracker.setMetadata('category', user.category);
            if (user.title) tracker.setMetadata('title', user.title);
            tracker.setMetadata('team', 'creighton');
          }
        })
        .catch((err) => {
          console.warn('[OpenReplay] Tracker failed to start — session not recorded.', err?.message || err);
        });
    }

    // Stop recording on logout
    if (!isAuthenticated && startedRef.current) {
      tracker.stop();
      startedRef.current = false;
    }

    return () => { cancelled = true; };
  }, [isAuthenticated, user, loading]);

  return getTracker();
}
